import { Button, Input, Message, Modal } from '@mairistem/composants';
import { ListeTable } from '@mairistem/liste';
import React from 'react';
import type { FluxPesEnAttente } from '../../services/pes/importPes';
import {
	filtrerFlux,
	type TriFluxEnAttente,
	trierFlux,
} from '../../utils/pes/fluxEnAttente';
import FluxEnAttenteRow from './FluxEnAttenteRow';

interface FluxEnAttenteModalProps {
	open: boolean;
	flux: FluxPesEnAttente[];
	chargement: boolean;
	erreur: string | null;
	onClose: () => void;
	onChoisir: (flux: FluxPesEnAttente) => void;
}

/**
 * Choix d'un flux PES déposé par un logiciel métier (cm_action) pour l'exercice, sur le modèle du choix d'imputation :
 * recherche, liste triable par colonne (plusieurs tris cumulables), une ligne choisie puis « Choisir ».
 */
const FluxEnAttenteModal = ({
	open,
	flux,
	chargement,
	erreur,
	onClose,
	onChoisir,
}: FluxEnAttenteModalProps) => {
	const [recherche, setRecherche] = React.useState('');
	const [tri, setTri] = React.useState<TriFluxEnAttente>({});
	const [choisi, setChoisi] = React.useState<FluxPesEnAttente | null>(null);
	const visibles = React.useMemo(
		() => trierFlux(filtrerFlux(flux, recherche), tri),
		[flux, recherche, tri],
	);

	// la liste est démontée à la fermeture (ses en-têtes perdent leur tri) : recherche, tri et choix repartent à zéro
	const fermer = () => {
		setRecherche('');
		setTri({});
		setChoisi(null);
		onClose();
	};

	const choisir = () => {
		if (choisi) {
			onChoisir(choisi);
			fermer();
		}
	};

	return (
		<Modal closeIcon onClose={fermer} open={open} size="large">
			<Modal.Header content="Choix d'un fichier en attente" />
			<Modal.Content>
				{erreur ? (
					<Message negative role="alert">
						{erreur}
					</Message>
				) : (
					<>
						<Input
							fluid
							icon="search"
							placeholder="Rechercher une source, un type, une date…"
							aria-label="Rechercher un fichier en attente"
							value={recherche}
							debounced={false}
							onChange={(
								_event: React.ChangeEvent<HTMLInputElement>,
								{ value }: { value: string },
							) => setRecherche(value)}
						/>
						<div style={{ height: '50vh' }}>
							<ListeTable
								items={visibles}
								loading={chargement}
								noResultsMessage={
									flux.length === 0
										? 'Aucun fichier en attente pour cet exercice.'
										: 'Aucun fichier ne correspond à la recherche.'
								}
								onSelect={(element: FluxPesEnAttente) => setChoisi(element)}
								onSort={(nouveau) => setTri(nouveau as TriFluxEnAttente)}
								striped={false}
							>
								<ListeTable.Header withDefaultColumn={false}>
									<ListeTable.Column name="codeSrc" sortable width={5}>
										Source
									</ListeTable.Column>
									<ListeTable.Column name="nom" sortable width={5}>
										Type
									</ListeTable.Column>
									<ListeTable.Column name="dateCreation" sortable width={3}>
										Date de création
									</ListeTable.Column>
									<ListeTable.Column name="taille" sortable width={3}>
										Taille
									</ListeTable.Column>
								</ListeTable.Header>
								{
									((element: FluxPesEnAttente) => (
										<FluxEnAttenteRow
											key={element.identifiant}
											item={element}
											selectedItem={choisi?.identifiant}
										/>
									)) as unknown as React.ReactNode
								}
							</ListeTable>
						</div>
					</>
				)}
			</Modal.Content>
			<Modal.Actions>
				<Button type="button" onClick={fermer}>
					Annuler
				</Button>
				<Button positive type="button" disabled={!choisi} onClick={choisir}>
					Choisir
				</Button>
			</Modal.Actions>
		</Modal>
	);
};

export default FluxEnAttenteModal;
